import { api } from '@/lib/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ChangeEvent } from 'react';
import { useRef } from 'react';
import { toast } from 'sonner';

interface ImportTsvResult {
  total: number;
  succeeded: number;
  failed: number;
}

const TSV_UPLOAD_FIELD = 'file' as const;
const MAX_TSV_BYTES = 10 * 1024 * 1024; // 10 MB — align with server limit
// Browsers set an empty MIME type for some TSV files; plain text is also acceptable.
const VALID_TSV_MIME_TYPES = new Set(['text/tab-separated-values', 'text/plain', '']);

export function useImportGroupTsv(groupId: string) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const mutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append(TSV_UPLOAD_FIELD, file);
      const result = await api.postForm<ImportTsvResult>(`/groups/${groupId}/import-tsv`, formData);
      return result;
    },
    onSuccess: async result => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['groups', groupId, 'registrations'] }),
        queryClient.invalidateQueries({ queryKey: ['registrations'] }),
        queryClient.invalidateQueries({ queryKey: ['groups'] }),
      ]);

      const { total, succeeded, failed } = result;
      if (failed > 0) {
        toast.warning(`TSV import klaar: ${succeeded}/${total} gelukt, ${failed} mislukt (zie Mislukte Inzendingen)`);
      } else {
        toast.success(`TSV import klaar: ${succeeded}/${total} gelukt`);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset before the guard so the same file can be re-selected after an error.
    event.target.value = '';

    if (!file) return;

    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext !== 'tsv' && !VALID_TSV_MIME_TYPES.has(file.type)) {
      toast.error('Selecteer een .tsv bestand');
      return;
    }

    if (file.size > MAX_TSV_BYTES) {
      toast.error('Bestand is te groot (max 10 MB)');
      return;
    }

    mutation.mutate(file);
  };

  return { fileInputRef, handleFileSelected, isPending: mutation.isPending };
}
